// /admin/integrations/retention — Sprint 7.
// Mostra esecuzioni passate e permette run-now.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, bulkExportFullUrl } from '../../lib/api';
import type { RetentionExecutionRowDto } from '@audiq/shared';

export function RetentionPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin', 'retention-executions'],
    queryFn: api.listRetentionExecutions,
  });
  const runMu = useMutation({
    mutationFn: api.runRetentionNow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'retention-executions'] }),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
          Retention dati
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--mute)' }}>
          Il cron retention gira ogni notte alle 03:00. Cancella encounter più vecchi di
          retention_days (configurabile da Configurazione → Retention) a meno che non siano marcati
          come clinical-relevant. Cancella anche test session orfane &gt; 24h.
        </p>

        <button
          onClick={() => {
            if (
              window.confirm('Eseguire retention adesso? Questa azione cancella encounter vecchi.')
            ) {
              runMu.mutate();
            }
          }}
          disabled={runMu.isPending}
          className="rounded-md px-5 py-2 text-sm font-medium"
          style={{
            background: 'var(--primary)',
            color: '#fff',
            opacity: runMu.isPending ? 0.6 : 1,
          }}
        >
          {runMu.isPending ? 'Esecuzione…' : 'Esegui retention ora'}
        </button>

        {runMu.error && (
          <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>
            {String(runMu.error)}
          </p>
        )}

        <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--line)' }}>
          <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
            Export completo dataset
          </h3>
          <p className="text-sm mb-4" style={{ color: 'var(--mute)' }}>
            Master only. Genera un ZIP con tutte le tabelle del DB (con identità decifrate). Per
            audit, migrazione, backup applicativo.
          </p>
          <a
            href={bulkExportFullUrl}
            download
            onClick={(e) => {
              if (
                !window.confirm(
                  'Confermi export completo? Il file conterrà identità decifrate. Conservalo in modo sicuro.',
                )
              ) {
                e.preventDefault();
              }
            }}
            className="rounded-md px-5 py-2 text-sm font-medium inline-block"
            style={{ background: 'var(--accent)', color: 'var(--ink)' }}
          >
            ⬇ Scarica export completo
          </a>
        </div>
      </div>

      <div>
        <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
          Esecuzioni recenti
        </h3>
        {list.isLoading ? (
          <p className="mono" style={{ color: 'var(--mute)' }}>
            Caricamento…
          </p>
        ) : (
          <div
            className="rounded-md border"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <ul>
              {list.data?.length === 0 && (
                <li className="p-4 text-sm" style={{ color: 'var(--mute)' }}>
                  Nessuna esecuzione registrata.
                </li>
              )}
              {list.data?.map((e: RetentionExecutionRowDto) => (
                <li
                  key={e.id}
                  className="p-3 text-xs"
                  style={{ borderBottom: '1px solid var(--line)' }}
                >
                  <div className="flex justify-between">
                    <span className="mono" style={{ color: 'var(--ink)' }}>
                      {new Date(e.executed_at).toLocaleString('it-IT')}
                    </span>
                    <span className="mono" style={{ color: 'var(--mute)' }}>
                      {e.triggered_by}
                    </span>
                  </div>
                  <div className="mt-1" style={{ color: 'var(--mute)' }}>
                    cancellati: <strong>{e.encounters_deleted}</strong> · skipped clinical:{' '}
                    {e.encounters_skipped_clinical} · test sessions: {e.test_sessions_deleted} ·
                    durata: {e.duration_ms}ms
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
